import { Link, Outlet, useLocation } from 'react-router-dom';
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { canAccessScreen, screenForPath } from '../../config/screenRegistry';
import { screens as canvasScreens, webPath } from '@screens';
import { withDesignSwitch } from '../../canvas/routes';

/**
 * Canvas boards (packages/screens + design/canvas) render 1:1 design previews with sample data: no data, no
 * writes, in-canvas simulated state only. They are reachable by any signed-in user; their eventual real
 * workflows must register in screenRegistry with a permission before showing data.
 */
const canvasShellPaths = new Set(canvasScreens.filter((s) => s.device !== 'phone').map((s) => webPath(s)));

/**
 * Central client-side navigation guard for every protected browser screen.
 * Server RBAC, warehouse membership, PostgreSQL RLS and RPC checks remain the
 * actual authorization boundary. Hiding a screen does not grant or revoke data.
 */
export default function ScreenAccessGate() {
  const { pathname } = useLocation();
  const user = useAuthStore((state) => state.user);
  const screen = screenForPath(pathname);

  if (!screen && (canvasShellPaths.has(pathname) || pathname.startsWith('/design/'))) {
    return withDesignSwitch(pathname, <Outlet />);
  }

  if (!screen) {
    return <div className="mx-auto max-w-xl rounded-xl border border-navy-200 bg-white p-8" role="status">
      <ExclamationTriangleIcon className="mb-4 h-9 w-9 text-navy-400" aria-hidden="true"/>
      <h1 className="text-2xl font-semibold text-navy-900">Screen not found</h1>
      <p className="mt-2 text-sm text-navy-600">
        This route does not exist in WareHouse. The application will not silently redirect a broken
        operational link to a different workflow.
      </p>
      <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
        <Link className="text-warehouse-700 underline-offset-4 hover:underline" to="/screens">Browse existing screens</Link>
        <Link className="text-warehouse-700 underline-offset-4 hover:underline" to="/work">Operations Center</Link>
      </div>
    </div>;
  }

  if (!canAccessScreen(screen, user?.role, user?.permissions)) {
    return <div className="mx-auto max-w-xl rounded-xl border border-amber-200 bg-white p-8" role="alert">
      <LockClosedIcon className="mb-4 h-9 w-9 text-amber-700" aria-hidden="true"/>
      <h1 className="text-2xl font-semibold text-navy-900">Access restricted</h1>
      <p className="mt-2 text-sm text-navy-600">
        Your account does not have the <strong>{screen.permission || 'required'}</strong> permission
        to open <strong>{screen.title}</strong>. Ask an authorized warehouse administrator to review
        your access. Never share another user's session or credentials.
      </p>
      <Link className="mt-5 inline-flex text-sm font-semibold text-warehouse-700 hover:underline"
        to="/work">Return to permitted work</Link>
    </div>;
  }

  return withDesignSwitch(pathname, <Outlet />);
}
